import React, { useContext, useState } from 'react'
import moment from 'moment'
//@MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import SchoolIcon from '@mui/icons-material/School'
import VisibilityIcon from '@mui/icons-material/Visibility'

import API from '../../../../services/API'
import BigModal from '../../../../components/Modals/BigModal'
import SmallModal from '../../../../components/Modals/SmallModal'
import useDocumentPreview, {
  extensionOf,
} from '../../../../components/Documents/useDocumentPreview'
import MandatoryTrainingApproval from './MandatoryTrainingApproval'
import {
  getExpiryDate,
  getRecordStatus,
  isELearningRecord,
  isLegacyELearning,
} from '../../../../helpers/mandatoryTrainingStatus'
import { GlobalContext } from '../../../../App'
import {
  catalogueCourseFor,
  requiredPathwayTitle,
} from '../../../../training/components/admin/catalogueHelpers'
import { downloadCertificate } from '../../../../training/components/admin/downloadCertificate'

// Full history of one course for one employee: every record with its status,
// certificate download, approval controls, and edit/delete for records that
// were entered by hand (e-Learning results are system evidence: read-only).
// Results from the retired e-learning are listed as history but not counted.
const MandatoryTrainingPopup = ({
  selectedCourse,
  setSelectedCourse,
  employee,
  catalogueCourses = [],
  onAddRecord,
  onEditRecord,
  onDeleted,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [downloading, setDownloading] = useState(null)
  const { openPreview, previewModal } = useDocumentPreview()
  const { setToast } = useContext(GlobalContext)

  if (!selectedCourse) return null

  const records = (employee.employee.mandatoryTraining || [])
    .filter((record) => record.course === selectedCourse._id)
    .sort((a, b) => moment(b.date) - moment(a.date))

  // true, or the id of the pathway this role must complete
  const requirement = selectedCourse.requiredBy?.[employee.employee.clinicalRole]
  const requiredForRole = !!requirement
  const catalogueCourse = catalogueCourseFor(catalogueCourses, selectedCourse.catalogueCode)
  const requiredPathway = requiredForRole ? requiredPathwayTitle(requirement, catalogueCourse) : null

  // Stored keys are random, so give the preview (and its download) a name
  const viewCertificate = (key) =>
    openPreview({
      key,
      name: `${selectedCourse.title} certificate.${extensionOf(key) || 'pdf'}`,
    })

  const handleDelete = () => {
    setDeleting(true)
    API.delete(`/employees/deleteTrainingRecord?id=${confirmDelete._id}`)
      .then((res) => {
        if (res.status === 200) {
          setConfirmDelete(null)
          onDeleted?.()
        }
      })
      .finally(() => setDeleting(false))
  }

  const handleDownloadCertificate = async (record) => {
    setDownloading(record._id)
    const error = await downloadCertificate(record.eLearning.certificate)
    setDownloading(null)
    if (error) setToast({ message: error, severity: 'error' })
  }

  // What the e-learning recorded about a result (both engines)
  const Details = ({ record, legacy, newEngine }) => {
    const result = record.eLearning ?? {}
    const pathway = catalogueCourse?.pathways?.find((entry) => entry.id === record.pathwayId)?.title
    const attempts = result.attempts
      ? `${result.attempts} attempt${result.attempts > 1 ? 's' : ''}`
      : null
    return (
      <Stack gap={0.5} alignItems="flex-start">
        {newEngine && (
          <>
            <Chip
              size="small"
              color="anMainBlue"
              sx={{ borderRadius: 1 }}
              icon={<SchoolIcon />}
              label="E-learning"
            />
            <Typography variant="body2" color="text.secondary">
              {[
                result.total
                  ? `Score ${result.score}/${result.total} (${Math.round((result.score / result.total) * 100)}%)`
                  : null,
                attempts,
                pathway,
                result.courseVersion ? `version ${result.courseVersion}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </>
        )}
        {legacy && (
          <Typography variant="body2" color="text.secondary">
            From the previous e-learning. No longer counts.
            {result.percentage != null ? ` Scored ${result.percentage}%.` : ''}
          </Typography>
        )}
        {(record.note || (!legacy && !newEngine)) && (
          <Typography variant="body2" color="text.secondary">
            {record.note || '—'}
          </Typography>
        )}
      </Stack>
    )
  }

  const Row = ({ record }) => {
    const status = getRecordStatus(selectedCourse, record)
    const legacy = isLegacyELearning(record)
    const newEngine = record.source === 'elearning'
    const expiry =
      !record.requiresApproval && !record.rejected && !legacy
        ? getExpiryDate(selectedCourse, record)
        : null
    // E-learning results of either engine are system evidence: read-only
    const eLearning = legacy || newEngine || isELearningRecord(record)
    const details = <Details record={record} legacy={legacy} newEngine={newEngine} />
    return (
      <TableRow hover>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {moment(record.date).format('DD MMM YYYY')}
          </Typography>
          {expiry && (
            <Typography variant="caption" color="text.secondary">
              Valid until {expiry.format('DD MMM YYYY')}
            </Typography>
          )}
          {(legacy || newEngine) && (
            <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}>{details}</Box>
          )}
        </TableCell>
        <TableCell>
          {record.requiresApproval ? (
            <MandatoryTrainingApproval courseID={record._id} />
          ) : (
            <Chip
              size="small"
              label={status.label}
              color={status.color}
              sx={{ borderRadius: 1 }}
            />
          )}
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          {details}
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" justifyContent="flex-end">
            {newEngine && record.eLearning?.certificate && (
              <Tooltip title="Download the e-learning certificate (PDF)">
                <span>
                  <Button
                    size="small"
                    color="third"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadCertificate(record)}
                    disabled={downloading === record._id}
                  >
                    {downloading === record._id ? 'Preparing...' : 'Certificate'}
                  </Button>
                </span>
              </Tooltip>
            )}
            {record.certificate && (
              <Tooltip title="View certificate">
                <IconButton
                  size="small"
                  color="third"
                  onClick={() => viewCertificate(record.certificate)}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            )}
            {!eLearning && (
              <>
                <Tooltip title="Edit record">
                  <IconButton
                    size="small"
                    onClick={() => onEditRecord?.(selectedCourse, record)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete record">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setConfirmDelete(record)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <BigModal
      open={!!selectedCourse}
      onClose={() => setSelectedCourse(null)}
      style={{ padding: 3, width: { xs: 'calc(100vw - 24px)', sm: '80%', lg: 900 } }}
    >
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="start">
          <Stack>
            <Typography variant="title3" fontWeight={700}>
              {selectedCourse.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {employee.firstName} {employee.lastName} ·{' '}
              {employee.employee.clinicalRole || 'No clinical role'} ·{' '}
              {requiredForRole
                ? `required${requiredPathway ? ` (${requiredPathway})` : ''}, renewed ${(selectedCourse.frequency ?? 'as configured').toLowerCase()}`
                : 'not required for this role'}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setSelectedCourse(null)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        {records.length === 0 ? (
          <Typography color="text.secondary">
            No records for this course yet.
          </Typography>
        ) : (
          <Box overflow="auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Completed</b>
                  </TableCell>
                  <TableCell>
                    <b>Status</b>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <b>Notes</b>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => (
                  <Row key={record._id} record={record} />
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            color="third"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddRecord?.(selectedCourse)}
          >
            Add a Record
          </Button>
        </Stack>
      </Stack>

      <SmallModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        style={{ width: 320, padding: 2 }}
      >
        <Stack gap={2}>
          <Typography variant="title3" textAlign="center">
            Delete this record?
          </Typography>
          <Typography variant="body2" textAlign="center">
            {selectedCourse.title} completed{' '}
            {confirmDelete && moment(confirmDelete.date).format('DD MMM YYYY')}.
            This cannot be undone.
          </Typography>
          <Stack direction="row" gap={1} justifyContent="center">
            <Button onClick={() => setConfirmDelete(null)} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleting}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </SmallModal>

      {previewModal}
    </BigModal>
  )
}

export default MandatoryTrainingPopup
